/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Sujith Dachepally
 * Created: 04/11/24
 */
import { useSearchParams } from 'react-router-dom';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { defaultTableParams } from '@/utils/tableUtils';
import { Badge, Box, Divider, Flex, Text } from '@chakra-ui/react';
import { Column, ListTable } from '@/components/pages/dashboard/tiles/bottomTile/bottomTileTables/ListTable';
import { PaginationToolbar, TableSkeletonLoading } from '@/components/Table';
import { dateAdd, toTZUnawareISODateString } from '@/wpt-lib/utils/dateUtils';
import { FormDateInput } from '@/components/FormInputField/FormDateInput';
import { LabeledSelect } from '@/components/FormInputField';
import { useGetRequest } from '@/utils/useStatefulRequests';
import { ListResponse } from '@/wpt-lib/interfaces/listResponse';
import { Organisation } from '@/wpt-lib/interfaces/Organisation';
import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import {
    AuditLog,
    AuditLogChange,
    mapAuditLogChangeTypeToString,
    mapUserRoleToString,
    UserRole,
} from '@/wpt-lib/interfaces/AuditLog';
import { AxiosError } from 'axios';
import { formatName } from '@/wpt-lib/utils/formatName';

type QueryOptions = {
    start_date: string;
    end_date: string;
    organisation?: string;
    user_role?: string;
    event_type?: string;
    sort?: string;
    order?: 'ASC' | 'DESC';
};

const AdminAuditLog = () => {
    const [search, setSearch] = useSearchParams();
    const { currentAssignment } = useAssignment();
    const [pageSize, setPageSize] = useState(50);
    const [page, setPage] = useState(defaultTableParams.page);
    const [pages, setPages] = useState(1);
    const [focusPage, setFocusPage] = useState(false);

    const auditColumns: Column<AuditLog>[] = [
        {
            name: 'Date',
            getData: (logItem: AuditLog) => new Date(logItem.createdAt).toLocaleString(),
        },
        {
            name: 'Changed By',
            getData: (logItem: AuditLog) => formatName(logItem.changedBy?.user),
        },
        {
            name: 'Role',
            getData: (logItem: AuditLog) => mapUserRoleToString[logItem.userRole],
        },
        {
            name: 'Organisation Unit',
            getData: (logItem: AuditLog) => `${logItem.changedBy.organisation?.name}`,
        },
        {
            name: 'Event Type',
            getData: (logItem: AuditLog) => {
                return (
                    <Badge color={mapAuditLogChangeTypeToString[logItem.change].color}>
                        {mapAuditLogChangeTypeToString[logItem.change]['value']}
                    </Badge>
                );
            },
        },
        {
            name: 'Details',
            getData: (logItem: AuditLog) => logItem.message,
        },
    ];

    const now = new Date();
    const defaultQuery: QueryOptions = {
        start_date: toTZUnawareISODateString(dateAdd(now, { months: -1 })),
        end_date: toTZUnawareISODateString(now),
        organisation: 'All',
        user_role: 'All',
        sort: 'Date',
        order: 'DESC',
    };

    const [query, setQuery] = useState<QueryOptions>({
        start_date: search.get('start_date') ?? defaultQuery.start_date,
        end_date: search.get('end_date') ?? defaultQuery.end_date,
        organisation: search.get('organisation') ?? defaultQuery.organisation,
        user_role: search.get('user_role') ?? defaultQuery.user_role,
        event_type: search.get('event_type') ?? defaultQuery.event_type,
        sort: search.get('sort') ?? defaultQuery.sort,
        order: search.get('order') === 'ASC' ? 'ASC' : 'DESC',
    });

    const {
        error: orgLoadingError,
        loading: isOrgsLoading,
        responseData: organisations,
        makeRequest: makeOrganisationRequest,
    } = useGetRequest<ListResponse<Organisation>>();

    const { error, loading, responseData: audit_logs, makeRequest } = useGetRequest<ListResponse<AuditLog>>();

    const getLogs = async () => {
        try {
            const params = {
                page: page,
                pageSize: pageSize,
                ...query,
                sortedOption: {
                    orderByColumn: query.sort ?? defaultQuery.sort,
                    orderByOrientation: query.order ?? defaultQuery.order,
                },
                assignment: currentAssignment?.id,
            };
            const res = await makeRequest(`audit_log/filter`, {
                params,
            });
            setPages(res.data.info.pages);
            setPage(res.data.info.page);
        } catch {
            //handled by hook
        }
    };

    useEffect(() => {
        makeOrganisationRequest(`/organisation`, {
            params: {
                assignment: currentAssignment?.id,
                all: true,
            },
        });
    }, []);

    useEffect(() => {
        getLogs();
    }, [pageSize, page, pages, query]);

    if (loading) {
        return (
            <Box w={'full'} p={4}>
                <Box bg={'offWhite'} p={4} rounded={'md'}>
                    <TableSkeletonLoading noOfCols={auditColumns.length} noOfRows={5} />
                </Box>
            </Box>
        );
    }

    if (orgLoadingError) {
        return (
            <Flex justifyContent="center">
                <SimpleAlert title="Error" variant="subtle">
                    {`Could not load list of organisations`}
                </SimpleAlert>
            </Flex>
        );
    }

    const setQueryParam = <Param extends keyof QueryOptions>(param: Param, value: QueryOptions[Param]) => {
        setQueryParams({ [param]: value });
    };

    const setQueryParams = (update: Partial<QueryOptions>) =>
        setQuery((query) => {
            setTimeout(() => {
                const params = Object.fromEntries(
                    Object.entries({
                        ...query,
                        ...update,
                        tab: 'audit-log',
                    }).filter(([, v]) => v && v !== 'All'),
                );
                setSearch(params);
            });

            return { ...query, ...update };
        });

    const handleDateChange = (field: 'start_date' | 'end_date') => (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const updatedQuery = { ...query, [field]: value };

        if (
            !updatedQuery.start_date ||
            !updatedQuery.end_date ||
            new Date(updatedQuery.start_date) <= new Date(updatedQuery.end_date)
        ) {
            setQueryParam(field, value);
        }
    };

    const handleChange = (field: keyof QueryOptions, value: string | undefined) => {
        setQueryParam(field, value ? value.toString() : 'All');
    };

    return (
        <Box p={5}>
            <>
                {error && (
                    <Flex justifyContent="center">
                        <SimpleAlert title="Error" variant="subtle">
                            {`${((error as AxiosError).response?.data as { message?: string })?.message || { error }}`}
                        </SimpleAlert>
                    </Flex>
                )}
            </>

            <Flex gap={3} direction="column" mb={3}>
                <Flex alignItems="center" justifyContent="flex-start" flexWrap="wrap" rowGap={0} columnGap={3}>
                    <Text fontWeight="bold">Filters</Text>
                    <FormDateInput
                        handleChange={handleDateChange('start_date')}
                        value={query.start_date}
                        fieldName="start_date"
                        isReadonly={false}
                        label="Start Date"
                        max={query.end_date ?? defaultQuery.end_date}
                        formLabelProps={{
                            textColor: 'black',
                            fontWeight: 'normal',
                            whiteSpace: 'nowrap',
                            margin: 0,
                        }}
                    />
                    <FormDateInput
                        handleChange={handleDateChange('end_date')}
                        value={query.end_date}
                        fieldName="end_date"
                        isReadonly={false}
                        label="End Date"
                        min={query.start_date ?? defaultQuery.start_date}
                        formLabelProps={{
                            textColor: 'black',
                            fontWeight: 'normal',
                            whiteSpace: 'nowrap',
                            margin: 0,
                        }}
                    />
                    <LabeledSelect
                        label={'Role:'}
                        onChange={(ev) =>
                            handleChange('user_role', ev.target.value !== 'all' ? ev.target.value : undefined)
                        }
                        value={query.user_role}
                        minW="100px"
                        width="fit-content"
                        controlProps={{ width: 'inherit' }}
                    >
                        <option value="all">All</option>
                        {Object.entries(UserRole)
                            .filter(([, role]) => !(role === UserRole.ACADEMIC || role === UserRole.FACULTY_ADMIN))
                            .map(([key, value]) => (
                                <option value={value} key={key}>
                                    {mapUserRoleToString[value]}
                                </option>
                            ))}
                    </LabeledSelect>
                    <LabeledSelect
                        label={'Event Type:'}
                        onChange={(ev) =>
                            handleChange('event_type', ev.target.value !== 'all' ? ev.target.value : undefined)
                        }
                        value={query.event_type}
                        minW="100px"
                        width="fit-content"
                        controlProps={{ width: 'inherit' }}
                    >
                        <option value="all">All</option>
                        {Object.entries(AuditLogChange)
                            .sort((a, b) =>
                                mapAuditLogChangeTypeToString[a[1]].value.localeCompare(
                                    mapAuditLogChangeTypeToString[b[1]].value,
                                ),
                            )
                            .map(([key, value]) => (
                                <option value={value} key={key}>
                                    {mapAuditLogChangeTypeToString[value].value}
                                </option>
                            ))}
                    </LabeledSelect>
                    <LabeledSelect
                        label={'Organisation:'}
                        onChange={(ev) =>
                            handleChange('organisation', ev.target.value !== 'all' ? ev.target.value : undefined)
                        }
                        value={query.organisation}
                        minW="100px"
                        width="fit-content"
                        controlProps={{ width: 'inherit' }}
                        isDisabled={isOrgsLoading}
                    >
                        <option value="all">All</option>
                        {organisations?.items
                            .filter((org: Organisation) => org.name !== 'System')
                            .map(({ id, name }) => (
                                <option value={`${id}`} key={id}>
                                    {name}
                                </option>
                            ))}
                    </LabeledSelect>
                </Flex>
                <Divider />
                <Flex direction="row" gap={3} alignItems="center" justifyContent="flex-start" flexWrap="wrap">
                    <Text fontWeight="bold">Sort</Text>
                    <LabeledSelect
                        label={'Sort By:'}
                        onChange={(ev) => handleChange('sort', ev.target.value !== 'all' ? ev.target.value : undefined)}
                        value={query.sort}
                        controlProps={{ width: 'inherit' }}
                        width="fit-content"
                    >
                        <option value="Date">Date</option>
                    </LabeledSelect>
                    <LabeledSelect
                        label={'Order By:'}
                        onChange={(ev) =>
                            handleChange('order', ev.target.value !== 'all' ? ev.target.value : undefined)
                        }
                        value={query.order}
                        controlProps={{ width: 'inherit' }}
                    >
                        <option value="ASC">Ascending</option>
                        <option value="DESC">Descending</option>
                    </LabeledSelect>
                </Flex>
                <Divider />
            </Flex>

            <Box overflowX={'auto'}>
                <ListTable
                    tableCaption={`List of audit logs (page ${page + 1} of ${pages === 0 ? 1 : pages})`}
                    getActions={() => []}
                    columns={auditColumns}
                    data={audit_logs?.items}
                    hideOption={true}
                />
            </Box>
            <Box overflowX={'auto'}>
                <PaginationToolbar
                    setPageSize={setPageSize}
                    setNewPage={setPage}
                    pageSize={pageSize}
                    pages={pages}
                    page={page}
                    focusPage={focusPage}
                    setFocusPage={setFocusPage}
                />
            </Box>
        </Box>
    );
};

export default AdminAuditLog;
